<style scoped>
h3 {
    font-size: 1.1em;
    font-weight: 600;
    margin-top: 10px;
}
.table {
    margin-bottom: 0;
}
::v-deep(.header span:after) {
    background-image: none;
}
::v-deep(.easy-data-table__rows-selector ul.select-items.show) {
    --easy-table-rows-per-page-selector-z-index: 3;
}
</style>
<template>
    <div class="admin-content">
        <LoadingPlaceholder v-if="!loaded" />
        <div class="row" v-if="loaded">
            <div class="col-sm-12">
                <div class="form-inline ml-auto float-right small" role="region" aria-label="Filters">
                    <label class="mr-3"><input type="checkbox" name="includePageDetails" v-model="includePageDetails" />&nbsp; Include Page Details</label>
                    <label v-show="canSeeAllTenants" class="mr-3"><input type="checkbox" v-model="includeAllTenants" />&nbsp; Show All Tenants</label>
                    <label class="mr-4"><input type="checkbox" v-model="rollupTenants" />&nbsp; Rollup</label>
                    <input type="date" class="form-control form-control-sm" id="statStartPeriod" v-model.lazy="startDate" />
                    &nbsp; - &nbsp;
                    <input type="date" class="form-control form-control-sm" id="statEndPeriod" v-model.lazy="endDate" />
                    <label class="sr-only" for="statStartPeriod">Start Date</label>
                    <label class="sr-only" for="statEndPeriod">End Date</label>
                </div>
                <h1 class="admin-content-heading">Stats</h1>
                <VueDataTable :items="rolledUpTenants" :headers="tableHeaders" class="vue-data-table table table-sm" role="main" aria-label="Statistics" theme-color="#3f51b5" :prevent-context-menu-row="false">
                    <template #item-totalBill="tenant">{{ formatAsCurrency(tenant.totalBill) }}</template>
                    <template #expand="tenant">
                        <template v-if="includePageDetails">
                            <h3>Page Details</h3>
                            <VueDataTable :items="tenant.pages" :headers="pageDetailsHeaders" sort-by="lastPublished" sort-type="desc" class="vue-data-table table" theme-color="#3f51b5" :prevent-context-menu-row="false">
                                <template #item-initiallyPublished="page">{{ localeDateTime(page.initiallyPublished) }}</template>
                                <template #item-lastPublished="page">{{ localeDateTime(page.lastPublished) }}</template>
                                <template #empty-message><div style="text-align: center">No pages</div></template>
                            </VueDataTable>
                        </template>
                        <h3>Usage Details</h3>
                        <VueDataTable :items="tenant.usages" :headers="usageHeaders" sort-by="datetime" sort-type="desc" class="vue-data-table table" theme-color="#3f51b5" :prevent-context-menu-row="false">
                            <template #item-datetime="usage">{{ localeDateTime(usage.datetime) }}</template>
                            <template #empty-message><div style="text-align: center">No usages</div></template>
                        </VueDataTable>
                        <h3>Bill Details</h3>
                        <VueDataTable :items="tenant.bills" :headers="billHeaders" sort-by="dateTime" sort-type="desc" class="vue-data-table table" theme-color="#3f51b5" :prevent-context-menu-row="false">
                            <template #item-dateTime="bill">{{ localeDateTime(bill.dateTime) }}</template>
                            <template #item-periodStart="bill">{{ localeDateTime(bill.periodStart) }}</template>
                            <template #item-periodEnd="bill">{{ localeDateTime(bill.periodEnd) }}</template>
                            <template #empty-message><div style="text-align: center">No bills</div></template>
                        </VueDataTable>
                    </template>
                </VueDataTable>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, watch, onActivated } from "vue";
import { computedAsync } from "@vueuse/core";
import getData from "../../lib/getData";
import VueDataTable, { Header } from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import LoadingPlaceholder from "@/lib/LoadingPlaceholder.3.vue";
import { localeDate, localeDateTime } from "@/lib/localeDatetime.3";
import formatAsCurrency from "@/lib/currency.3";
import { useMainStore } from "@/mainStore";
import { AllTenantStatsViewModel } from "@cs/AllTenantStatsViewModel";
import { AllTenantStatsViewModel as JsAllTenantStatsViewModel } from "@cs-ts/all-tenant-stats-view-model";
import { UsageViewModel as JsUsageViewModel } from "@cs-ts/usage-view-model";
type __JsTenant = NonNullable<JsAllTenantStatsViewModel["tenants"]>[0];
type JsTenant = Omit<__JsTenant, "usages"> & { totalBill: number; maxBillableUsers: number; usages: JsUsageViewModel[] };
export default defineComponent({
    //mixins: [dateFilterMixin, currencyFilterMixin],
    components: {
        VueDataTable,
        LoadingPlaceholder,
    },
    setup() {
        const mainStore = useMainStore();
        const tenants = ref([] as JsTenant[]);
        const loaded = ref(false);
        const includePageDetails = ref(false);
        const includeAllTenants = ref(false);
        const rollupTenants = ref(true);
        const startDate = ref(null as null | string);
        const endDate = ref(null as null | string);
        //const adminUrl = computedAsync(() => {
        //    return mainStore.getUrl("admin");
        //});
        const canSeeAllTenants = computedAsync(async () => {
            return (await mainStore.userDataPromise).CanViewAllTenantsStats;
        });
        const rolledUpTenants = computed(() => {
            let tenantsV = JSON.parse(JSON.stringify(tenants.value)) as NonNullable<(typeof tenants)["value"]>;
            if (rollupTenants.value) {
                const tenantChildrenDict = tenantsV.reduce<Record<string, JsTenant[]>>((a, i) => {
                    a[i.parentCode!] = a[i.parentCode!] || [];
                    a[i.parentCode!]!.push(i);
                    return a;
                }, {});
                tenantsV = tenantsV.filter(i => i.parentCode == null || !tenantsV.some(j => j.code == i.parentCode));
                const addSubtenant = (root: JsTenant, child: JsTenant) => {
                    root.bills = (root.bills || []).concat(child.bills || []);
                    root.pages = (root.pages || []).concat(child.pages || []);
                    root.usages = (root.usages || []).concat(child.usages || []);
                    root.maxBillableUsers = Math.max(root.maxBillableUsers, child.maxBillableUsers);
                    root.totalBill += child.totalBill;
                    root.allCount += child.allCount;
                    root.editedCount += child.editedCount;
                    root.createdCount += child.createdCount;
                    if (child.code! in tenantChildrenDict) {
                        tenantChildrenDict[child.code!].forEach(subchild => addSubtenant(root, subchild));
                    }
                };
                tenantsV.forEach(root => {
                    if (root.code! in tenantChildrenDict) {
                        tenantChildrenDict[root.code!].forEach(child => addSubtenant(root, child));
                    }
                });
            }
            const tenantDict = tenantsV.reduce<Record<string, JsTenant>>((a, i) => {
                a[i.code!] = i;
                return a;
            }, {});
            const sortKey = (tenant: JsTenant): string => (tenant.parentCode != null && tenant.parentCode in tenantDict ? sortKey(tenantDict[tenant.parentCode]) + "`" : "") + tenant.label;
            let tenantSort = tenantsV.map(i => [sortKey(i), i] as const);
            tenantSort.sort((a, b) => (a[0] < b[0] ? -1 : a[0] == b[0] ? 0 : 1));
            tenantsV = tenantSort.map(i => i[1]);
            return tenantsV;
        });
        const load = async () => {
            loaded.value = false;
            const statsData = (await getData(
                mainStore,
                "AdminStats",
                () => mainStore.getUrl("adminStatsData", { includePageDetails: includePageDetails.value, includeAllTenants: includeAllTenants.value, start: startDate.value, end: endDate.value }),
                data => data.IncludePageDetails == includePageDetails.value && data.IncludeAllTenants == includeAllTenants.value && (startDate.value == null || new Date(startDate.value) == new Date(data.RangeStart)) && (endDate.value == null || new Date(endDate.value) == new Date(data.RangeEnd)),
            )) as AllTenantStatsViewModel;
            const rootTenantsData = statsData.Tenants!;
            tenants.value = rootTenantsData.map(
                eD =>
                    ({
                        rangeEnd: eD.RangeEnd,
                        rangeStart: eD.RangeStart,
                        code: eD.Code,
                        parentCode: eD.ParentCode,
                        label: eD.Label,
                        allCount: eD.AllCount,
                        editedCount: eD.EditedCount,
                        createdCount: eD.CreatedCount,
                        pages: (eD.Pages || []).map(
                            eP =>
                                ({
                                    label: eP.Label,
                                    type: eP.Type,
                                    initiallyPublished: eP.InitiallyPublished,
                                    lastPublished: eP.LastPublished,
                                    hasPayment: eP.HasPayment,
                                }) as NonNullable<JsTenant["pages"]>[0],
                        ),
                        usages: Object.values(eD.Usages || {})
                            .flat()
                            .map(
                                eP =>
                                    ({
                                        type: eP.Type,
                                        datetime: eP.Datetime,
                                        count: eP.Count,
                                    }) as JsUsageViewModel,
                            ),
                        bills: (eD.Bills || []).map(
                            eP =>
                                ({
                                    type: eP.Type,
                                    dateTime: eP.DateTime,
                                    amount: eP.Amount,
                                    periodStart: eP.PeriodStart,
                                    periodEnd: eP.PeriodEnd,
                                    memo: eP.Memo,
                                }) as NonNullable<JsTenant["bills"]>[0],
                        ),
                        totalBill: (eD.Bills || []).map(i => i.Amount!).reduce((i, a) => i + a, 0),
                        maxBillableUsers: ((eD.Usages || {})["Billable Users"] || []).map(i => i.Count).reduce((i, a) => Math.max(i, a), 0),
                    }) as JsTenant,
            );
            const tdn = (i: number) => (i < 10 ? "0" + i : "" + i);
            const ymd = (i: Date) => i.getFullYear() + "-" + tdn(i.getMonth() + 1) + "-" + tdn(i.getDate());
            startDate.value = ymd(new Date(statsData.RangeStart));
            endDate.value = ymd(new Date(statsData.RangeEnd));
            //tenants.value.sort((a,b) => a.label < b.label ? -1 : a.label == b.label ? 0 : 1);
            loaded.value = true;
        };
        watch(
            () => includePageDetails.value,
            () => load(),
        );
        watch(
            () => includeAllTenants.value,
            () => load(),
        );
        watch(
            () => startDate.value,
            (_, oldValue) => {
                if (oldValue != null) {
                    load();
                }
            },
        );
        watch(
            () => endDate.value,
            (_, oldValue) => {
                if (oldValue != null) {
                    load();
                }
            },
        );
        onActivated(() => {
            load();
        });

        const alwaysTableHeaders: Header[] = [
            { text: "Label", value: "label" },
            { text: "Max Billable Users", value: "maxBillableUsers" },
            { text: "Total Bill", value: "totalBill" },
        ];
        const pageTableHeaders: Header[] = [
            { text: "Page Count", value: "allCount" },
            { text: "Page Created", value: "createdCount" },
            { text: "Page Edited", value: "editedCount" },
        ];
        const tableHeaders = computed(() => {
            if (includePageDetails.value) {
                return [...alwaysTableHeaders, ...pageTableHeaders];
            } else {
                return [...alwaysTableHeaders];
            }
        });

        const pageDetailsHeaders: Header[] = [
            { text: "Label", value: "label", sortable: true },
            { text: "Type", value: "type", sortable: true },
            { text: "Created", value: "initiallyPublished", sortable: true },
            { text: "Updated", value: "lastPublished", sortable: true },
            { text: "Has Payment", value: "hasPayment", sortable: true },
        ];
        const usageHeaders: Header[] = [
            { text: "Type", value: "type", sortable: true },
            { text: "Date", value: "dateTime", sortable: true },
            { text: "Count", value: "count", sortable: true },
        ];
        const billHeaders: Header[] = [
            { text: "Type", value: "type", sortable: true },
            { text: "Date", value: "dateTime", sortable: true },
            { text: "Period Start", value: "periodStart", sortable: true },
            { text: "Period End", value: "periodEnd", sortable: true },
            { text: "Amount", value: "amount", sortable: true },
            { text: "Memo", value: "memo", sortable: true },
        ];

        return {
            load,
            loaded,
            includePageDetails,
            canSeeAllTenants,
            includeAllTenants,
            rollupTenants,
            startDate,
            endDate,
            rolledUpTenants,

            tableHeaders,
            pageDetailsHeaders,
            usageHeaders,
            billHeaders,

            formatAsCurrency,
            localeDate,
            localeDateTime,
        };
    },
});
</script>
